/**
 * Accounts API (TMMAPP-0008 / TMMAPP-0011, design §8.6) — wire types from `src/api/generated/`;
 * normalized into the module's domain type. Bank-side amounts are in the account's currency;
 * `operatingBalanceMinor` / `allocatedMinor` / `unallocatedMinor` are in the owner's operating
 * currency (MM-D-63). `GET /v1/me/accounts` lists owned and granted rows.
 */
import { download, request, type DownloadResult } from './client';
import { personNameOrNull } from './personNames';
import { ApiShapeError } from './errors';
import type {
  AccountConnectorBlock as WireAccountConnector,
  AccountConnectorPending as WireAccountConnectorPending,
  AccountLastImport as WireLastImport,
  AccountReorderRequest,
  AccountResponse as WireAccount,
  Balances as WireBalances,
  BalanceCheck as WireBalanceCheck,
  BalanceCheckPage as WireBalanceCheckPage,
  InstitutionProposalConfirmRequest,
  LoanScheduleResponse as WireLoanSchedule,
  LoanScheduleRow as WireLoanScheduleRow,
  LoanTerms as WireLoanTerms,
  MyPermissions as WireMyPermissions,
  PermissionResponse as WirePermission,
} from './generated';

export type AccountType = 'CASH' | 'REVOLVING' | 'LOAN';
export type AccountSubtype =
  'CHECKING' | 'SAVINGS' | 'OTHER' | 'CREDIT_CARD' | 'LINE_OF_CREDIT' | 'MORTGAGE' | 'SIMPLE';
export const SUBTYPES_BY_TYPE: Record<AccountType, AccountSubtype[]> = {
  CASH: ['CHECKING', 'SAVINGS', 'OTHER'],
  REVOLVING: ['CREDIT_CARD', 'LINE_OF_CREDIT'],
  LOAN: ['MORTGAGE', 'SIMPLE'],
};
export type AccountStatus = 'OPEN' | 'CLOSED';

export interface LoanTerms {
  originalMinor: number;
  originatedOn: string;
  paymentMinor: number;
  paymentDay: number;
  maturesOn: string;
}

export interface MyPermissions {
  canImport: boolean;
  canViewFull: boolean;
  canEdit: boolean;
}

export interface AccountBalances {
  bankBalanceMinor: number | null;
  operatingBalanceMinor: number | null;
  allocatedMinor: number | null;
  unallocatedMinor: number | null;
  statement: number | null;
  aprBp: number | null;
  balance: number | null;
  amortized: number | null;
  maturesOn: string | null;
  /** Civil or instant the balances were taken; null when the wire omits it. */
  asOf: string | null;
}

/** One grant on an owned account, as My Accounts' permission chip names it (TMMAPI-0186 U3). */
export interface AccountGrantee {
  /** `null` when the grantee has no display name (render with `personName`). */
  displayName: string | null;
  canEdit: boolean;
  canViewFull: boolean;
}

export interface AccountLastImport {
  at: string;
  profileName: string | null;
  transactionCount: number;
}

export type AccountAccess = 'OWNER' | 'GRANTEE';
/** Where the opening balance came from (TMMAPI-0160 U2); `PROVIDER` is the bank connection's. */
export type OpeningSource = NonNullable<WireAccount['openingSource']>;
export type AccountManagement = 'MANAGED' | 'UNMANAGED';

/** An open or historical balance check (TMMAPI-0165, MM-D-144). `source` and `status` are open strings. */
export interface BalanceCheck {
  id: number;
  on: string;
  bankBalanceMinor: number;
  ledgerBalanceMinor: number;
  differenceMinor: number;
  source: string;
  importBatchId: number | null;
  status: string;
  firstDivergenceOn: string | null;
  firstDivergenceTransactionId: number | null;
  firstDivergenceFileMinor: number | null;
  firstDivergenceLedgerMinor: number | null;
  constantFromStart: boolean;
  adjustmentTransactionId: number | null;
  createdAt: string;
}

export interface AccountConnectorPending {
  postedOn: string;
  payee: string;
  amountMinor: number;
}

export interface AccountConnector {
  itemId: number | null;
  institutionName: string;
  mask: string | null;
  status: string;
  lastSyncedAt: string | null;
  bankBalanceMinor: number | null;
  balanceAsOf: string | null;
  pending: AccountConnectorPending[];
}

export interface Account {
  aiMatchAutoApplyBp: number | null;
  id: number;
  name: string;
  type: AccountType;
  subtype: AccountSubtype;
  institution: string | null;
  last4: string | null;
  currency: string | null;
  operatingCurrency: string | null;
  status: AccountStatus | null;
  openingBalanceMinor: number | null;
  openingBalanceOn: string | null;
  /**
   * Created without an opening balance (TMMAPI-0160 U2): its own balances are null (shown "—") until
   * the opening is set, from the first import or the edit form. Absent on the wire → false.
   */
  openingPending: boolean;
  /** Null when the wire omits it (a summary view) or sends an unknown value. */
  openingSource: OpeningSource | null;
  /**
   * True once confirmed, or when the opening came from the person or the provider. Null when the
   * wire omits it: {@link openingIsConfirmed} then applies that same rule. False while pending
   * and for an unconfirmed file or running-balance opening.
   */
  openingConfirmed: boolean | null;
  /** The open balance check, or null. */
  balanceCheck: BalanceCheck | null;
  /** Civil date of the latest balanced, adjusted, or opening-fixed check. Null when none. */
  balancedThroughOn: string | null;
  balancedBalanceMinor: number | null;
  coveredThrough: string | null;
  aprBp: number | null;
  loan: LoanTerms | null;
  myPermissions: MyPermissions;
  balances: AccountBalances | null;
  access: AccountAccess;
  capabilities: string[];
  ownerDisplayName: string | null;
  connector: AccountConnector | null;
  /** Absent on the wire → `'MANAGED'` (TMMAPP-0026 / 0023 default rule). */
  management: AccountManagement;
  includeInNetWorth: boolean;
  /** Civil date; null when unset (nothing rendered). */
  budgetStartOn: string | null;
  /** Lines before budget start; null/0 means no history line. */
  historicalLineCount: number | null;
  /** Last file import of this account; null when the wire omits it. */
  lastImport: AccountLastImport | null;
  /**
   * The people the account is granted to, by name, in grant order (TMMAPI-0186 U3): the owner's view
   * on `GET /v1/me/accounts` (empty when granted to no one); null where it isn't served (an account
   * granted to the caller, a single-account read).
   */
  grantees: AccountGrantee[] | null;
  /** Server order among owned accounts of this type. */
  sortOrder: number;
  /** IBAN, CLABE or account number (TMMAPI-0165). Absent or null when the wire omits it. */
  externalIdentifier?: string | null;
  /** `IBAN`, `CLABE` or `ACCOUNT` when the server recognised one; absent or null otherwise. */
  externalIdentifierKind?: string | null;
  /**
   * The catalog institution (TMMAPI-0195 U2, MM-D-152), beside the free-text `institution`. Absent
   * or null when none is set (or on a view that doesn't serve it).
   */
  institutionId?: number | null;
  /**
   * `institutionId`'s catalog row (TMMAPI-0196 U5): served wherever `institutionId` is, for an
   * inactive institution too. Its name is what shows as the account's bank ({@link institutionLabel});
   * the free-text `institution` stays the owner's own. Absent or null when none is set.
   */
  institutionCatalog?: InstitutionCatalog | null;
  /**
   * The matching job's proposal for the free-text institution: the owner answers Yes (confirm) or
   * No (dismiss); it is never applied without them. The owner's full view only; absent or null when
   * none stands.
   */
  institutionProposal?: InstitutionProposal | null;
}

/** An account's catalog institution, as served beside `institutionId` (TMMAPI-0196 U5). */
export interface InstitutionCatalog {
  id: number;
  /** The catalog's name, never the owner's free text. */
  name: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
}

/**
 * The bank an account shows (TMMAPP-0165 item 17): the catalog name when `institutionId` is set and
 * its catalog row is served, else the owner's free text. Null when there is neither.
 */
export function institutionLabel(
  account: Pick<Account, 'institution' | 'institutionId' | 'institutionCatalog'>,
): string | null {
  const catalog = account.institutionCatalog ?? null;
  if (account.institutionId != null && catalog !== null && catalog.name !== '') return catalog.name;
  return account.institution;
}

/** A proposed catalog institution for an account's typed bank name (TMMAPI-0195 U2 item 8). */
export interface InstitutionProposal {
  institutionId: number;
  /** Its catalog name. */
  name: string;
}

export interface AccountCreateRequest {
  type: AccountType;
  subtype: AccountSubtype;
  name: string;
  institution?: string | undefined;
  /**
   * The catalog institution picked (TMMAPP-0163 item 11). A pick sends the id alone: `institution`
   * is the owner's free text, sent only when they typed it (TMMAPP-0165 item 17).
   */
  institutionId?: number | undefined;
  last4?: string | undefined;
  currency?: string | undefined;
  /** Both or neither (TMMAPI-0160 U2): without them the account is created with its opening pending. */
  openingBalanceMinor?: number;
  openingBalanceOn?: string;
  aprBp?: number;
  loan?: LoanTerms;
  management?: AccountManagement | undefined;
  includeInNetWorth?: boolean | undefined;
  /** Optional IBAN, CLABE or account number. The server derives `last4` when it is omitted. */
  externalIdentifier?: string | undefined;
}

export type AccountPatchRequest = Partial<
  Pick<
    AccountCreateRequest,
    | 'name'
    | 'institution'
    | 'last4'
    | 'currency'
    | 'openingBalanceMinor'
    | 'openingBalanceOn'
    | 'aprBp'
    | 'loan'
    | 'management'
    | 'includeInNetWorth'
    | 'externalIdentifier'
  >
> & {
  /** Civil date, or `null` to clear. */
  budgetStartOn?: string | null;
  aiMatchAutoApplyBp?: number | null;
  /** A catalog institution's id sets it; `null` clears it (TMMAPI-0195 U2). Omit to leave it. */
  institutionId?: number | null;
};

export interface Permission {
  userId: number;
  displayName: string | null;
  canImport: boolean;
  canViewFull: boolean;
  canEdit: boolean;
}

export interface PermissionFlags {
  canImport: boolean;
  canViewFull: boolean;
  canEdit: boolean;
}

export interface LoanScheduleRow {
  paymentOn: string;
  paymentMinor: number;
  interestMinor: number;
  principalMinor: number;
  balanceAfterMinor: number;
}

export interface LoanSchedule {
  rows: LoanScheduleRow[];
  paidToDate: string | null;
  computedPayoffOn: string | null;
}

const ACCOUNT_TYPES: ReadonlySet<string> = new Set(['CASH', 'REVOLVING', 'LOAN']);
const OPENING_SOURCES: ReadonlySet<string> = new Set([
  'USER',
  'FILE_STATEMENT',
  'RUNNING_BALANCE',
  'PROVIDER',
]);
const ACCOUNT_SUBTYPES: ReadonlySet<string> = new Set([
  'CHECKING',
  'SAVINGS',
  'OTHER',
  'CREDIT_CARD',
  'LINE_OF_CREDIT',
  'MORTGAGE',
  'SIMPLE',
]);

function normalizeLoan(w: WireLoanTerms): LoanTerms {
  return {
    originalMinor: w.originalMinor,
    originatedOn: w.originatedOn,
    paymentMinor: w.paymentMinor,
    paymentDay: w.paymentDay,
    maturesOn: w.maturesOn,
  };
}

function normalizePermissions(w: WireMyPermissions): MyPermissions {
  return { canImport: w.canImport, canViewFull: w.canViewFull, canEdit: w.canEdit };
}

/** Optional on the wire, but never guessed: an account without it is a shape error. */
function normalizeAccess(value: WireAccount['access']): AccountAccess {
  if (value == null) throw new ApiShapeError('access is required');
  return value === 'GRANTEE' ? 'GRANTEE' : 'OWNER';
}

/** Every pending field is optional on the wire; a row without a date or amount is dropped. */
function normalizePending(w: WireAccountConnectorPending): AccountConnectorPending | null {
  if (w.postedOn == null || w.amountMinor == null) return null;
  return { postedOn: w.postedOn, payee: w.payee ?? '', amountMinor: w.amountMinor };
}

function normalizeAccountConnector(w: WireAccountConnector): AccountConnector {
  return {
    itemId: w.itemId ?? null,
    institutionName: w.institutionName ?? '',
    mask: w.mask ?? null,
    status: w.status ?? '',
    lastSyncedAt: w.lastSyncedAt ?? null,
    bankBalanceMinor: w.bankBalanceMinor ?? null,
    balanceAsOf: w.balanceAsOf ?? null,
    pending: (w.pending ?? []).flatMap((row) => {
      const item = normalizePending(row);
      return item ? [item] : [];
    }),
  };
}

function normalizeBalances(w: WireBalances): AccountBalances {
  return {
    bankBalanceMinor: w.bankBalanceMinor ?? null,
    operatingBalanceMinor: w.operatingBalanceMinor ?? null,
    allocatedMinor: w.allocatedMinor ?? null,
    unallocatedMinor: w.unallocatedMinor ?? null,
    statement: w.statement ?? null,
    aprBp: w.aprBp ?? null,
    balance: w.balance ?? null,
    amortized: w.amortized ?? null,
    maturesOn: w.maturesOn ?? null,
    asOf: w.asOf ?? null,
  };
}

function normalizeBalanceCheck(w: WireBalanceCheck): BalanceCheck {
  return {
    id: w.id,
    on: w.on,
    bankBalanceMinor: w.bankBalanceMinor,
    ledgerBalanceMinor: w.ledgerBalanceMinor,
    differenceMinor: w.differenceMinor,
    source: w.source,
    importBatchId: w.importBatchId ?? null,
    status: w.status,
    firstDivergenceOn: w.firstDivergenceOn ?? null,
    firstDivergenceTransactionId: w.firstDivergenceTransactionId ?? null,
    firstDivergenceFileMinor: w.firstDivergenceFileMinor ?? null,
    firstDivergenceLedgerMinor: w.firstDivergenceLedgerMinor ?? null,
    constantFromStart: w.constantFromStart ?? false,
    adjustmentTransactionId: w.adjustmentTransactionId ?? null,
    createdAt: w.createdAt,
  };
}

/**
 * Whether the starting balance is confirmed. An omitted flag follows the server: pending is not
 * confirmed, a file or running-balance opening is not until the flag is true, and a user or
 * provider opening (or an older fixture with no source) is.
 */
export function openingIsConfirmed(account: {
  openingPending: boolean;
  openingConfirmed: boolean | null;
  openingSource: OpeningSource | null;
}): boolean {
  if (account.openingPending) return false;
  if (account.openingConfirmed !== null) return account.openingConfirmed;
  return account.openingSource !== 'FILE_STATEMENT' && account.openingSource !== 'RUNNING_BALANCE';
}

function normalizeLastImport(w: WireLastImport): AccountLastImport {
  return { at: w.at, profileName: w.profileName ?? null, transactionCount: w.transactionCount };
}

/** An account row; `null` (dropped, warned in DEV) with a type this build doesn't know. */
export function normalizeAccount(w: WireAccount): Account | null {
  if (!ACCOUNT_TYPES.has(w.type)) {
    if (import.meta.env.DEV) console.warn('accounts: unknown type dropped', w.type);
    return null;
  }
  const type: AccountType = w.type;
  const subtype = ACCOUNT_SUBTYPES.has(w.subtype) ? w.subtype : SUBTYPES_BY_TYPE[type][0]!;
  return {
    id: w.id,
    name: w.name,
    type,
    subtype,
    institution: w.institution ?? null,
    last4: w.last4 ?? null,
    currency: w.currency ?? null,
    operatingCurrency: w.operatingCurrency ?? null,
    status: w.status === 'CLOSED' ? 'CLOSED' : w.status === 'OPEN' ? 'OPEN' : null,
    openingBalanceMinor: w.openingBalanceMinor ?? null,
    openingBalanceOn: w.openingBalanceOn ?? null,
    openingPending: w.openingPending ?? false,
    openingSource:
      w.openingSource != null && OPENING_SOURCES.has(w.openingSource) ? w.openingSource : null,
    openingConfirmed: w.openingConfirmed ?? null,
    balanceCheck: w.balanceCheck == null ? null : normalizeBalanceCheck(w.balanceCheck),
    balancedThroughOn: w.balancedThroughOn ?? null,
    balancedBalanceMinor: w.balancedBalanceMinor ?? null,
    coveredThrough: w.coveredThrough ?? null,
    aprBp: w.aprBp ?? null,
    loan: w.loan == null ? null : normalizeLoan(w.loan),
    myPermissions: normalizePermissions(w.myPermissions),
    balances: w.balances == null ? null : normalizeBalances(w.balances),
    access: normalizeAccess(w.access),
    capabilities: w.capabilities ?? [],
    ownerDisplayName: personNameOrNull(w.ownerDisplayName),
    connector: w.connector == null ? null : normalizeAccountConnector(w.connector),
    management: w.management === 'UNMANAGED' ? 'UNMANAGED' : 'MANAGED',
    includeInNetWorth: w.includeInNetWorth ?? false,
    budgetStartOn: w.budgetStartOn ?? null,
    historicalLineCount: w.historicalLineCount ?? null,
    aiMatchAutoApplyBp: w.aiMatchAutoApplyBp ?? null,
    lastImport: w.lastImport == null ? null : normalizeLastImport(w.lastImport),
    grantees:
      w.grantees === undefined
        ? null
        : w.grantees.map((g) => ({
            displayName: personNameOrNull(g.displayName),
            canEdit: g.canEdit,
            canViewFull: g.canViewFull,
          })),
    sortOrder: w.sortOrder,
    externalIdentifier: w.externalIdentifier ?? null,
    externalIdentifierKind: w.externalIdentifierKind ?? null,
    institutionId: w.institutionId ?? null,
    institutionCatalog:
      w.institutionCatalog == null
        ? null
        : {
            id: w.institutionCatalog.id,
            name: w.institutionCatalog.name,
            country: w.institutionCatalog.country,
          },
    institutionProposal: normalizeInstitutionProposal(w.institutionProposal),
  };
}

function normalizeInstitutionProposal(
  w: WireAccount['institutionProposal'],
): InstitutionProposal | null {
  return w == null ? null : { institutionId: w.institutionId, name: w.name };
}

export function normalizeAccounts(value: readonly WireAccount[]): Account[] {
  return value.flatMap((row) => {
    const account = normalizeAccount(row);
    return account ? [account] : [];
  });
}

function required(wire: WireAccount): Account {
  const account = normalizeAccount(wire);
  if (!account) throw new ApiShapeError(`account.type ${wire.type} is unknown`);
  return account;
}

export function normalizePermission(w: WirePermission): Permission {
  return {
    userId: w.userId,
    displayName: personNameOrNull(w.displayName),
    canImport: w.canImport,
    canViewFull: w.canViewFull,
    canEdit: w.canEdit,
  };
}

export function normalizePermissionsList(value: readonly WirePermission[]): Permission[] {
  return value.map(normalizePermission);
}

function normalizeScheduleRow(w: WireLoanScheduleRow): LoanScheduleRow {
  return {
    paymentOn: w.paymentOn,
    paymentMinor: w.paymentMinor,
    interestMinor: w.interestMinor,
    principalMinor: w.principalMinor,
    balanceAfterMinor: w.balanceAfterMinor,
  };
}

export function normalizeLoanSchedule(w: WireLoanSchedule): LoanSchedule {
  return {
    rows: w.rows.map(normalizeScheduleRow),
    paidToDate: w.paidToDate ?? null,
    computedPayoffOn: w.computedPayoffOn ?? null,
  };
}

export const accountsApi = {
  /** GET /v1/me/accounts — owned and granted. */
  async list(): Promise<Account[]> {
    return normalizeAccounts(await request<WireAccount[]>('GET', '/v1/me/accounts'));
  },
  /** GET /v1/me/accounts/{id}. */
  async get(id: number): Promise<Account> {
    return required(await request<WireAccount>('GET', `/v1/me/accounts/${id}`));
  },
  /** POST /v1/me/accounts. */
  async create(body: AccountCreateRequest): Promise<Account> {
    return required(await request<WireAccount>('POST', '/v1/me/accounts', { json: body }));
  },
  /** PATCH /v1/me/accounts/{id} — only the changed fields. */
  async update(id: number, body: AccountPatchRequest): Promise<Account> {
    return required(
      await request<WireAccount>('PATCH', `/v1/me/accounts/${id}`, {
        json: body,
      }),
    );
  },
  /**
   * POST /v1/me/accounts/{id}/institution/confirm — the owner's Yes to the proposal they saw: the
   * proposed id is sent, so a stale screen never confirms another (`409 INSTITUTION_PROPOSAL_STATE`).
   */
  async confirmInstitution(id: number, institutionId: number): Promise<Account> {
    return required(
      await request<WireAccount>('POST', `/v1/me/accounts/${id}/institution/confirm`, {
        json: { institutionId } satisfies InstitutionProposalConfirmRequest,
      }),
    );
  },
  /** POST /v1/me/accounts/{id}/institution/dismiss — the owner's No; it isn't proposed again. */
  async dismissInstitution(id: number): Promise<Account> {
    return required(
      await request<WireAccount>('POST', `/v1/me/accounts/${id}/institution/dismiss`),
    );
  },
  /** POST /v1/me/accounts/{id}/close. */
  async close(id: number): Promise<void> {
    await request<unknown>('POST', `/v1/me/accounts/${id}/close`);
  },
  /** GET /v1/me/accounts/{id}/permissions. */
  async permissions(id: number): Promise<Permission[]> {
    return normalizePermissionsList(
      await request<WirePermission[]>('GET', `/v1/me/accounts/${id}/permissions`),
    );
  },
  /** PUT /v1/me/accounts/{id}/permissions/{userId}. */
  async putPermission(id: number, userId: number, flags: PermissionFlags): Promise<Permission> {
    return normalizePermission(
      await request<WirePermission>('PUT', `/v1/me/accounts/${id}/permissions/${userId}`, {
        json: flags,
      }),
    );
  },
  /** DELETE /v1/me/accounts/{id}/permissions/{userId}. */
  async revokePermission(id: number, userId: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/me/accounts/${id}/permissions/${userId}`);
  },
  /** GET /v1/me/accounts/{id}/loan-schedule. */
  async loanSchedule(id: number): Promise<LoanSchedule> {
    return normalizeLoanSchedule(
      await request<WireLoanSchedule>('GET', `/v1/me/accounts/${id}/loan-schedule`),
    );
  },
  /** POST /v1/me/accounts/{id}/opening/confirm — no body. The answer is a check; refetch the account. */
  async confirmOpening(id: number): Promise<BalanceCheck> {
    return normalizeBalanceCheck(
      await request<WireBalanceCheck>('POST', `/v1/me/accounts/${id}/opening/confirm`),
    );
  },
  /** POST /v1/me/accounts/{id}/balance-checks. A difference of 0 is BALANCED. */
  async recordBalanceCheck(
    id: number,
    body: { bankBalanceMinor: number; on: string },
  ): Promise<BalanceCheck> {
    return normalizeBalanceCheck(
      await request<WireBalanceCheck>('POST', `/v1/me/accounts/${id}/balance-checks`, {
        json: body,
      }),
    );
  },
  /** GET /v1/me/accounts/{id}/balance-checks — newest first. */
  async listBalanceChecks(
    id: number,
    query: { cursor?: string; limit?: number } = {},
  ): Promise<{ items: BalanceCheck[]; nextCursor: string | null }> {
    const page = await request<WireBalanceCheckPage>(
      'GET',
      `/v1/me/accounts/${id}/balance-checks`,
      { query },
    );
    return { items: page.items.map(normalizeBalanceCheck), nextCursor: page.page.nextCursor };
  },
  /** POST …/balance-checks/{checkId}/adjust. 409 BALANCE_CHECK_NOT_OPEN when it is not open. */
  async adjustBalanceCheck(id: number, checkId: number): Promise<BalanceCheck> {
    return normalizeBalanceCheck(
      await request<WireBalanceCheck>(
        'POST',
        `/v1/me/accounts/${id}/balance-checks/${checkId}/adjust`,
      ),
    );
  },
  /** POST …/balance-checks/{checkId}/dismiss. */
  async dismissBalanceCheck(id: number, checkId: number): Promise<BalanceCheck> {
    return normalizeBalanceCheck(
      await request<WireBalanceCheck>(
        'POST',
        `/v1/me/accounts/${id}/balance-checks/${checkId}/dismiss`,
      ),
    );
  },
  /** POST /v1/me/accounts/reorder — no UI; 0096 adopts it. */
  async reorder(type: AccountReorderRequest['type'], accountIds: number[]): Promise<Account[]> {
    return normalizeAccounts(
      await request<WireAccount[]>('POST', '/v1/me/accounts/reorder', {
        json: { type, accountIds } satisfies AccountReorderRequest,
      }),
    );
  },
  /** GET /v1/me/accounts/{id}/export.xlsx?from&to. */
  async exportXlsx(
    id: number,
    window: { from?: string | undefined; to?: string | undefined } = {},
  ): Promise<DownloadResult> {
    return download(`/v1/me/accounts/${id}/export.xlsx`, {
      query: { from: window.from, to: window.to },
    });
  },
};
